import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Code, Save, X } from 'lucide-react';
import api from '../../services/api';
import { useForm, Controller } from 'react-hook-form';

interface Service {
  id: number;
  title: string;
  description: string;
  features: string[];
  icon: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ServiceFormData {
  title: string;
  description: string;
  features: string;
  icon: string;
  isActive: boolean;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { register, handleSubmit, reset, control, setValue, formState: { errors } } = useForm<ServiceFormData>();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/admin/services');
      setServices(response.data.services || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setMessage({ type: 'error', text: 'Failed to load services' });
      setLoading(false);
    }
  };

  const onSubmit = async (data: ServiceFormData) => {
    try {
      const featuresArray = data.features
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0);

      const serviceData = {
        title: data.title,
        description: data.description,
        features: featuresArray,
        icon: data.icon || 'Code',
        isActive: data.isActive
      };

      if (editingService) {
        await api.put(`/admin/services/${editingService.id}`, serviceData);
        setMessage({ type: 'success', text: 'Service updated successfully' });
      } else {
        await api.post('/admin/services', serviceData);
        setMessage({ type: 'success', text: 'Service created successfully' });
      }

      fetchServices();
      handleCancel();
    } catch (error: any) {
      console.error('Error saving service:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save service' });
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setShowForm(true);
    setValue('title', service.title);
    setValue('description', service.description);
    setValue('features', service.features.join('\n'));
    setValue('icon', service.icon);
    setValue('isActive', service.isActive);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await api.delete(`/admin/services/${id}`);
      setMessage({ type: 'success', text: 'Service deleted successfully' });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      setMessage({ type: 'error', text: 'Failed to delete service' });
    }
  };

  const handleCancel = () => {
    setEditingService(null);
    setShowForm(false);
    reset({
      title: '',
      description: '',
      features: '',
      icon: 'Code',
      isActive: true
    });
  };

  const moveService = async (index: number, direction: 'up' | 'down') => {
    const newServices = [...services];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newServices.length) return;
    
    [newServices[index], newServices[targetIndex]] = [newServices[targetIndex], newServices[index]];
    
    try {
      await api.post('/admin/services/reorder', {
        services: newServices.map((s, idx) => ({ id: s.id, displayOrder: idx }))
      });
      setServices(newServices);
      setMessage({ type: 'success', text: 'Services reordered successfully' });
    } catch (error) {
      console.error('Error reordering services:', error);
      setMessage({ type: 'error', text: 'Failed to reorder services' });
    }
  };

  const toggleActive = async (service: Service) => {
    try {
      await api.put(`/admin/services/${service.id}`, {
        ...service,
        isActive: !service.isActive
      });
      setMessage({ type: 'success', text: `Service ${!service.isActive ? 'activated' : 'deactivated'}` });
      fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
      setMessage({ type: 'error', text: 'Failed to toggle service status' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Service Management</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showForm ? <X className="h-5 w-5 mr-2" /> : <Plus className="h-5 w-5 mr-2" />}
          {showForm ? 'Cancel' : 'Add New Service'}
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('title', { required: 'Title is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Features (one per line) <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('features', { required: 'At least one feature is required' })}
                rows={5}
                placeholder="Custom Design&#10;Responsive Layout&#10;Brand Integration&#10;User Experience"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.features && <p className="text-red-500 text-sm mt-1">{errors.features.message}</p>}
              <p className="text-sm text-gray-500 mt-1">Enter each feature on a new line</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon Name (Lucide React)
              </label>
              <input
                type="text"
                {...register('icon')}
                placeholder="Code"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Icon name from <a href="https://lucide.dev" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Lucide React</a> (e.g., Code, Palette, ShoppingCart)
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isActive')}
                id="isActive"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                Active (display on website)
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingService ? 'Update Service' : 'Create Service'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Features
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Icon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {services.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No services found. Click "Add New Service" to create one.
                </td>
              </tr>
            ) : (
              services.map((service, index) => (
                <tr key={service.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveService(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => moveService(index, 'down')}
                        disabled={index === services.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{service.title}</div>
                    <div className="text-sm text-gray-500 line-clamp-2">{service.description}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-700">
                      {service.features.length} feature{service.features.length !== 1 ? 's' : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Code className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">{service.icon}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActive(service)}
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        service.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {service.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(service)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceManagement;
